import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useUser } from '@/contexts/UserContext';
import { api } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DataTable, DataTableColumn, DataTableGroup, DataTableConfig } from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { Check, X, Clock, Eye } from 'lucide-react';
import { ApprovalStatus } from '../components/approval-status';

interface ApprovalRequest {
  id: string;
  entity_type: string;
  entity_id: string;
  requester_id: string;
  status: string;
  metadata: any;
  created_at: string;
  updated_at: string;
  requesterName?: string;
}

export default function ApprovalsManagement() {
  const { user, isLoading } = useUser();
  const [, setLocation] = useLocation();
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [selectedApproval, setSelectedApproval] = useState<ApprovalRequest | null>(null);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedApprovals, setSelectedApprovals] = useState<ApprovalRequest[]>([]);

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation('/login');
      return;
    }
    
    // Simple admin check (should be robust in real app)
    const userEmail = user?.email?.toLowerCase() || '';
    const adminEmails = ['admin@fotup.com', 'admin@devonno.com'];
    if (!isLoading && user && !adminEmails.includes(userEmail)) {
      toast({
        title: 'Acesso negado',
        description: 'Você não tem permissão para acessar esta página.',
        variant: 'destructive',
      });
      setLocation('/');
    }

    if (user) {
      fetchApprovals();
    }
  }, [isLoading, user, setLocation]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/approvals?all=true');
      if (response.data.success) {
        setApprovals(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar aprovações:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as aprovações.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approval: ApprovalRequest) => {
    try {
      setIsProcessing(true);
      const response = await api.post(`/api/approvals/${approval.id}/approve`, {});
      
      if (response.data?.success) {
        toast({
          title: 'Aprovado',
          description: 'Solicitação aprovada com sucesso.',
        });
        await fetchApprovals();
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error: any) {
      console.error('Erro ao aprovar:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível aprovar a solicitação.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveAll = async () => {
    const pendingApprovals = filteredApprovals.filter(a => a.status === 'PENDING');
    if (pendingApprovals.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Não há solicitações pendentes para aprovar.',
      });
      return;
    }

    try {
      setIsProcessing(true);
      const response = await api.post('/api/approvals/approve-all', { type: filterType === 'all' ? undefined : filterType });
      
      if (response.data?.success) {
        toast({
          title: 'Aprovado',
          description: response.data.message || `${response.data.approved} solicitação(ões) aprovada(s) com sucesso.`,
        });
        await fetchApprovals();
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error: any) {
      console.error('Erro ao aprovar todas:', error);
      toast({
        title: 'Erro',
        description: error?.message || 'Não foi possível aprovar todas as solicitações.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openRejectDialog = (approval: ApprovalRequest) => {
    setSelectedApproval(approval);
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };

  const openDetailsDialog = (approval: ApprovalRequest) => {
    setSelectedApproval(approval);
    setIsDetailsDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedApproval || !rejectionReason.trim()) return;

    try {
      setIsProcessing(true);
      const response = await api.post(`/api/approvals/${selectedApproval.id}/reject`, {
        reason: rejectionReason
      });
      
      if (response.data.success) {
        toast({
          title: 'Rejeitado',
          description: 'Solicitação rejeitada com sucesso.',
        });
        setIsRejectDialogOpen(false);
        fetchApprovals();
      }
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível rejeitar a solicitação.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredApprovals = approvals.filter(approval => 
    filterType === 'all' || approval.entity_type === filterType
  );

  // Agrupar por status
  const groupedApprovals = filteredApprovals.reduce((acc, approval) => {
    const status = approval.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(approval);
    return acc;
  }, {} as Record<string, ApprovalRequest[]>);

  // Ordenar grupos: PENDING primeiro, depois APPROVED, depois REJECTED
  const statusOrder = ['PENDING', 'APPROVED', 'REJECTED'];
  const sortedGroups = statusOrder.filter(status => groupedApprovals[status]?.length > 0);

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'PENDING': 'Pendentes',
      'APPROVED': 'Aprovadas',
      'REJECTED': 'Rejeitadas'
    };
    return labels[status] || status;
  };

  // Configurar grupos para DataTable
  const tableGroups: DataTableGroup<ApprovalRequest>[] = sortedGroups.map(status => ({
    key: status,
    label: getStatusLabel(status),
    items: groupedApprovals[status],
    defaultExpanded: status === 'PENDING',
  }));

  // Configurar colunas
  const columns: DataTableColumn<ApprovalRequest>[] = [
    {
      key: 'entity_type',
      label: 'Tipo',
      render: (item) => <Badge variant="outline" className="text-xs">{getEntityLabel(item.entity_type)}</Badge>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => <ApprovalStatus status={item.status} />,
    },
    {
      key: 'requester',
      label: 'Solicitante',
      render: (item) => (
        <>
          <div className="text-xs font-medium">{item.requesterName}</div>
          <div className="text-[10px] text-muted-foreground">ID: {item.requester_id.substring(0, 8)}...</div>
        </>
      ),
    },
    {
      key: 'details',
      label: 'Detalhes',
      render: (item) => (
        <div className="text-xs">
          {item.entity_type === 'EVENT' && (
            <span>Evento: <strong className="font-medium">{item.metadata?.title || 'Sem título'}</strong></span>
          )}
          {item.entity_type === 'SUBSCRIPTION' && (
            <span>Inscrição em Evento ID: {item.metadata?.eventId?.substring(0, 8)}...</span>
          )}
          {item.entity_type === 'USER' && (
            <span>
              {item.metadata?.userName || item.requesterName || 'Usuário'} 
              {item.metadata?.email && ` (${item.metadata.email})`}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Data',
      render: (item) => (
        <div className="flex items-center text-xs text-muted-foreground">
          <Clock className="w-3 h-3 mr-1" />
          {formatDate(item.created_at)}
        </div>
      ),
    },
  ];

  // Ações contextuais por linha
  const rowActions = (approval: ApprovalRequest) => (
    <>
      {approval.status === 'PENDING' && (
        <>
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white h-7 px-2 text-xs"
            onClick={() => handleApprove(approval)}
            disabled={isProcessing}
          >
            <Check className="w-3 h-3 mr-1" />
            Aprovar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            className="h-7 px-2 text-xs"
            onClick={() => openRejectDialog(approval)}
            disabled={isProcessing}
          >
            <X className="w-3 h-3 mr-1" />
            Rejeitar
          </Button>
        </>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={() => openDetailsDialog(approval)}
      >
        <Eye className="w-3 h-3 mr-1" />
        Detalhes
      </Button>
    </>
  );

  // Área de filtros
  const filterArea = (
    <div className="flex gap-2">
      <select
        className="px-2 py-1.5 text-sm border rounded-md bg-background"
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        aria-label="Filtrar por tipo"
      >
        <option value="all">Todos os tipos</option>
        <option value="EVENT">Eventos</option>
        <option value="SUBSCRIPTION">Inscrições</option>
        <option value="USER">Usuários</option>
      </select>
    </div>
  );

  // Área de ações
  const actionsArea = (
    <>
      {filteredApprovals.filter(a => a.status === 'PENDING').length > 0 && (
        <Button 
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white"
          onClick={handleApproveAll}
          disabled={isProcessing}
        >
          <Check className="w-4 h-4 mr-1" />
          Aprovar Todas
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={fetchApprovals}>
        Atualizar
      </Button>
    </>
  );

  const tableConfig: DataTableConfig = {
    showFilters: true,
    showCheckboxes: false, // Pode ser habilitado no futuro
    showGrouping: true,
    showFooter: true,
    compact: true,
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntityLabel = (type: string) => {
    const map: Record<string, string> = {
      'EVENT': 'Evento',
      'SUBSCRIPTION': 'Inscrição',
      'USER': 'Usuário',
      'PHOTO': 'Foto'
    };
    return map[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-7xl">
        <DataTable
          title="Gestão de Aprovações"
          description="Gerencie todas as solicitações de aprovação"
          data={filteredApprovals}
          columns={columns}
          groups={tableGroups}
          filterArea={filterArea}
          actionsArea={actionsArea}
          rowActions={rowActions}
          config={tableConfig}
          emptyMessage="Nenhuma solicitação encontrada"
          getItemId={(item) => item.id}
        />

        <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rejeitar Solicitação</DialogTitle>
              <DialogDescription>
                Por favor, informe o motivo da rejeição. Esta informação será visível para o solicitante.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Motivo da rejeição..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRejectDialogOpen(false)}>Cancelar</Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isProcessing}
              >
                Confirmar Rejeição
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalhes da Solicitação</DialogTitle>
              <DialogDescription>
                Informações completas da solicitação de aprovação
              </DialogDescription>
            </DialogHeader>
            {selectedApproval && (
              <div className="py-4 space-y-4">
                {/* Status destacado no topo */}
                <div className="flex items-center justify-center p-4 bg-muted/50 rounded-lg border">
                  <ApprovalStatus status={selectedApproval.status} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground">Tipo</label>
                    <p className="text-sm mt-1">
                      <Badge variant="outline">{getEntityLabel(selectedApproval.entity_type)}</Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground">Solicitante</label>
                    <p className="text-sm mt-1">{selectedApproval.requesterName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">ID: {selectedApproval.requester_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground">Data de Criação</label>
                    <p className="text-sm mt-1">{formatDate(selectedApproval.created_at)}</p>
                  </div>
                  {selectedApproval.updated_at !== selectedApproval.created_at && (
                    <div>
                      <label className="text-sm font-semibold text-muted-foreground">Última Atualização</label>
                      <p className="text-sm mt-1">{formatDate(selectedApproval.updated_at)}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-semibold text-muted-foreground">Detalhes</label>
                  <div className="mt-1 text-sm">
                    {selectedApproval.entity_type === 'EVENT' && (
                      <div className="space-y-2">
                        <p><strong>Evento:</strong> {selectedApproval.metadata?.title || 'Sem título'}</p>
                        {selectedApproval.metadata?.description && (
                          <p className="text-muted-foreground">{selectedApproval.metadata.description}</p>
                        )}
                      </div>
                    )}
                    {selectedApproval.entity_type === 'SUBSCRIPTION' && (
                      <p>Inscrição em Evento ID: {selectedApproval.metadata?.eventId || selectedApproval.entity_id}</p>
                    )}
                    {selectedApproval.entity_type === 'USER' && (
                      <div className="space-y-2">
                        <p><strong>Nome:</strong> {selectedApproval.metadata?.userName || selectedApproval.requesterName || 'Não informado'}</p>
                        {selectedApproval.metadata?.email && (
                          <p><strong>Email:</strong> {selectedApproval.metadata.email}</p>
                        )}
                        {selectedApproval.metadata?.userType && (
                          <p><strong>Tipo:</strong> {selectedApproval.metadata.userType === 'Photographer' ? 'Fotógrafo' : 'Atleta'}</p>
                        )}
                        <p><strong>Status Atual:</strong> {selectedApproval.metadata?.status || 'PendingApproval'}</p>
                      </div>
                    )}
                  </div>
                </div>
                {selectedApproval.metadata && Object.keys(selectedApproval.metadata).length > 0 && (
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground">Metadados</label>
                    <pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-auto max-h-40">
                      {JSON.stringify(selectedApproval.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDetailsDialogOpen(false)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
